/**
 * FilterBar — 列表頁通用篩選條（v3 "Quiet Refinement"）
 * ──────────────────────────────────────────────
 * 升級亮點：
 *   - 整體容器帶 subtle ring（var(--border-subtle)）+ 輕微背景（surface-muted）
 *   - search 為中心，更大更突出（優先級高於其他篩選項）
 *   - filters 區域統一 gap，讓各控件整齊排列
 *   - "清除篩選" 文字按鈕：低調但可見，配合 activeFilterCount 顯示
 *   - extra 右側對齊，分隔明確
 *
 * 使用場景：配合 ListPageShell.filters 插槽，或直接在頁面中使用。
 */
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { space, color, text, weight, radius, font } from '@/styles/tokens';

export interface FilterBarProps {
  /** 主搜尋（通常為 antd Input.Search） */
  search?: ReactNode;
  /** 篩選控件（傳多個 Select / DatePicker，會以 flex 排列） */
  filters?: ReactNode;
  /** 右側額外操作（如 refresh / 列管理） */
  extra?: ReactNode;
  /** 自定義 search 寬度，預設 300 */
  searchWidth?: number;
  /** 當前激活的篩選條數（用於顯示「清除篩選」鏈接） */
  activeFilterCount?: number;
  /** 清除篩選回調 */
  onClearFilters?: () => void;
}

export default function FilterBar({
  search,
  filters,
  extra,
  searchWidth = 300,
  activeFilterCount = 0,
  onClearFilters,
}: FilterBarProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: space[3],
        rowGap: space[2],
      }}
    >
      {/* 主搜尋框（視覺突出，寬度更大） */}
      {search && (
        <div style={{ width: searchWidth, maxWidth: '100%', flexShrink: 0 }}>{search}</div>
      )}

      {/* 次要篩選控件 */}
      {filters && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: space[2],
            flex: 1,
            minWidth: 0,
          }}
        >
          {filters}
        </div>
      )}

      {/* 清除篩選：有激活篩選時才顯示 */}
      {activeFilterCount > 0 && onClearFilters && (
        <button
          type="button"
          onClick={onClearFilters}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '0 4px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: text.sm,
            color: color.text.tertiary,
            fontWeight: weight.medium,
            fontFamily: font.body,
            borderRadius: radius.xs,
            transition: 'color 120ms cubic-bezier(0.16, 1, 0.3, 1)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = color.brand.primary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = color.text.tertiary;
          }}
          aria-label={`清除全部 ${activeFilterCount} 個篩選條件`}
        >
          <X size={12} strokeWidth={2} />
          清除篩選
          {activeFilterCount > 0 && (
            <span
              style={{
                marginLeft: 2,
                fontSize: text.xs,
                color: color.text.muted,
              }}
            >
              ({activeFilterCount})
            </span>
          )}
        </button>
      )}

      {/* 右側額外操作 */}
      {extra && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: space[2],
            marginLeft: 'auto',
            flexShrink: 0,
          }}
        >
          {extra}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────
 * FilterBarLabel — 篩選控件前的小標籤（可選，用於無 placeholder 的場景）
 * ──────────────────────────────────────────────── */
export function FilterBarLabel({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        fontSize: text.sm,
        color: color.text.tertiary,
        fontWeight: weight.medium,
        marginRight: 4,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/* ────────────────────────────────────────────────
 * FilterBarDivider — 篩選控件間的垂直分隔線
 * ──────────────────────────────────────────────── */
export function FilterBarDivider() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 1,
        height: 16,
        background: color.border.default,
        borderRadius: 1,
        flexShrink: 0,
      }}
    />
  );
}
